"use client";

import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowRight } from "lucide-react";
import AnimateOnScroll from "./AnimateOnScroll";
import CustomButton from "./CustomButton";
import { ThemeName, BUTTON_PRIMARY, GET_SUPPORT_GRADIENT } from "./theme";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";
import { trackBookAppointmentClicked } from "@/lib/analytics";

export interface ServiceShowcaseItem {
  title: string;
  description: string;
  image: string;
  href?: string;
}

const ServiceSlideCard = ({
  service,
  index,
  theme,
}: {
  service: ServiceShowcaseItem;
  index: number;
  theme: Exclude<ThemeName, "white">;
}) => (
  <article
    role="listitem"
    className="group relative h-[420px] w-[280px] shrink-0 overflow-hidden rounded-[16px] sm:h-[460px] sm:w-[340px] lg:h-[480px] lg:w-[calc((100%-64px)/3)]"
    aria-label={`Service: ${service.title}`}
  >
    <Image
      src={service.image}
      alt={service.title}
      quality={100}
      fill
      className="object-cover transition-transform duration-500 group-hover:scale-105"
      sizes="(max-width: 1024px) 340px, 33vw"
    />

    <div
      className="absolute inset-0"
      style={{ background: GET_SUPPORT_GRADIENT[theme] }}
    />

    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[10px] p-5">
      <span className="font-crimsonText text-[20px] font-semibold text-white/60">
        {String(index + 1).padStart(2, "0")}
      </span>
      <h3
        className="font-crimsonText text-2xl font-bold leading-[110%] text-white"
        style={{ letterSpacing: "-0.04em" }}
      >
        {service.title}
      </h3>
      <p className="line-clamp-3 font-onest text-[14px] font-light leading-[130%] text-white/80">
        {service.description}
      </p>

      <Link
        href={service.href || "#"}
        className={`mt-[10px] flex w-fit items-center gap-[11px] rounded-full py-[5px] pl-[13px] pr-[5px] ${BUTTON_PRIMARY[theme].colorClasses} ${BUTTON_PRIMARY[theme].borderClass}`}
        aria-label={`Learn more about ${service.title}`}
      >
        <span className="font-onest text-[14px] font-medium">Learn More</span>
        <span className="flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-full bg-white">
          <ArrowRight
            size={12}
            style={{ color: BUTTON_PRIMARY[theme].arrowColor }}
            strokeWidth={1.5}
          />
        </span>
      </Link>
    </div>
  </article>
);

interface ServicesShowcaseProps {
  heading?: React.ReactNode;
  description?: string;
  services: ServiceShowcaseItem[];
  viewAllLabel?: string;
  viewAllHref?: string;
  bookingLabel?: string;
  bookingHref?: string;
  theme?: Exclude<ThemeName, "white">;
  trackingLocation?: string;
}

const ServicesShowcase = ({
  heading = "Explore Our Counselling Services",
  description = "Seeking professional guidance is a strong, proactive step toward personal wellbeing. Working with an experienced counsellor in Sydney, Alex Rodriguez provides compassionate, evidence-based therapy tailored to your unique circumstances—helping you build practical coping strategies, gain deeper self-awareness, and achieve lasting mental wellness through in-person consultations and online telehealth sessions across Australia.",
  services,
  viewAllLabel = "View Counselling Services",
  viewAllHref = "/counselling",
  bookingLabel = "Book an Appointment",
  bookingHref = "/booking",
  theme = "purple",
  trackingLocation = "services_showcase",
}: ServicesShowcaseProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 1 | -1) => {
    if (!scrollRef.current) return;
    const firstCard = scrollRef.current.firstElementChild as HTMLElement;
    const cardW = firstCard ? firstCard.offsetWidth : 340;
    scrollRef.current.scrollBy({
      left: dir * (cardW + 20),
      behavior: "smooth",
    });
  };

  const headingText =
    typeof heading === "string" ? heading : "Explore Our Services";

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: headingText,
    provider: {
      "@type": "Organization",
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: headingText,
      itemListElement: services.map((service) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: service.title,
          description: service.description,
          ...(service.href
            ? { url: new URL(service.href, SITE_URL).href }
            : {}),
        },
      })),
    },
  };

  return (
    <section
      className="container bg-[#F7FAFF]"
      aria-labelledby="services-showcase-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(servicesSchema).replace(/</g, "\\u003c"),
        }}
      />

      {/* Header */}
      <AnimateOnScroll className="mb-8 flex flex-col items-start justify-between gap-6 sm:mb-10 lg:mb-12 lg:flex-row lg:items-end">
        <div className="flex max-w-[650px] flex-col gap-4">
          <h2
            id="services-showcase-heading"
            className="heading text-v3-secondary"
          >
            {heading}
          </h2>
          <p className="font-onest text-[14px] font-normal leading-[1.7] text-black/60 sm:text-[15px]">
            {description}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <CustomButton
            label={viewAllLabel}
            href={viewAllHref}
            variant="secondary"
            theme={theme}
          />
          <CustomButton
            label={bookingLabel}
            href={bookingHref}
            variant="primary"
            theme={theme}
            onClick={() =>
              trackBookAppointmentClicked({ location: trackingLocation })
            }
          />
        </div>
      </AnimateOnScroll>

      {/* Nav arrows + slider */}
      <div className="flex flex-col gap-4">
        <div
          className="flex justify-end gap-3"
          role="group"
          aria-label="Service navigation"
        >
          <button
            onClick={() => scroll(-1)}
            className="flex size-[40px] items-center justify-center rounded-[14px] bg-black/10 text-black/60 transition-colors hover:bg-black/15 lg:size-[50px] lg:rounded-[16px]"
            aria-label="Previous services"
          >
            <ArrowLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => scroll(1)}
            className="flex size-[40px] items-center justify-center rounded-[14px] bg-black/10 text-black/60 transition-colors hover:bg-black/15 lg:size-[50px] lg:rounded-[16px]"
            aria-label="Next services"
          >
            <ArrowRight size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex w-full gap-5 overflow-x-auto scroll-smooth [scrollbar-width:none] lg:gap-8 [&::-webkit-scrollbar]:hidden"
          role="list"
          aria-label={headingText}
        >
          {services.map((service, idx) => (
            <ServiceSlideCard
              key={service.title}
              service={service}
              index={idx}
              theme={theme}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesShowcase;
